import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '../components/ui/button';
import { Shield, CreditCard, Check, Loader2, Clock, Sparkles, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_STYLE = {
  style: {
    base: {
      color: '#e4e4e7',
      fontFamily: '"Inter", system-ui, sans-serif',
      fontSize: '16px',
      '::placeholder': { color: '#71717a' },
    },
    invalid: { color: '#ef4444' },
  },
};

const TRIAL_PERKS = [
  'Create AI influencers & generate content',
  'Access all creator tools for 7 days',
  'No charge during your trial period',
  'Cancel anytime before trial ends',
];

function SetupForm({ onSuccess }) {
  const { token } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API}/billing/setup-intent`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create setup intent');

      const { error: stripeError } = await stripe.confirmCardSetup(data.client_secret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (stripeError) throw new Error(stripeError.message);

      const confirmRes = await fetch(`${API}/billing/confirm-setup`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const confirmData = await confirmRes.json();
      if (!confirmRes.ok) throw new Error(confirmData.detail || 'Failed to confirm setup');

      onSuccess(confirmData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/80 p-4">
        <CardElement options={CARD_STYLE} />
      </div>

      {error && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-400" data-testid="card-error">
          {error}
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || loading}
        className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-base rounded-xl"
        data-testid="start-trial-btn"
      >
        {loading ? (
          <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Setting up...</>
        ) : (
          <><ArrowRight className="w-5 h-5 mr-2" /> Start Free 7-Day Trial</>
        )}
      </Button>

      <div className="flex items-center justify-center gap-2 text-xs text-zinc-500">
        <Shield className="w-3.5 h-3.5" />
        <span>Secured by Stripe. You won't be charged during the trial.</span>
      </div>
    </form>
  );
}

export default function TrialSetupPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    if (user.is_founder || user.subscription_status === 'subscribed' || user.subscription_status === 'trial_active') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleSuccess = async () => {
    toast.success('Trial activated! Welcome to Delphi AI.');
    await refreshUser();
    navigate('/dashboard', { replace: true });
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium">
            <Clock className="w-4 h-4" />
            7-Day Free Trial
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight" data-testid="trial-heading">
            Start Creating with Delphi AI
          </h1>
          <p className="text-zinc-400 text-base">
            Add a payment method to begin your free trial. No charges for 7 days.
          </p>
        </div>

        {/* Perks */}
        <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/40 p-5 space-y-3">
          {TRIAL_PERKS.map((perk) => (
            <div key={perk} className="flex items-start gap-3">
              <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              <span className="text-sm text-zinc-300">{perk}</span>
            </div>
          ))}
        </div>

        {/* Card Form */}
        <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/40 p-6 space-y-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-zinc-800">
              <CreditCard className="w-5 h-5 text-zinc-300" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Payment Method</h3>
              <p className="text-xs text-zinc-500">Required to start trial. Cancel anytime.</p>
            </div>
          </div>

          <Elements stripe={stripePromise}>
            <SetupForm onSuccess={handleSuccess} />
          </Elements>
        </div>

        <p className="text-center text-xs text-zinc-600">
          After your trial, choose a plan starting at $29/mo to keep creating.
        </p>
      </div>
    </div>
  );
}
